import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { AgedDebtBucketItem } from '../../services/apis/dashboard/dashboard.service';
import { ThemeService } from '../../services/theme.service';
import { chartColors } from './chart-theme';

@Component({
  selector: 'app-aged-debt-bucket-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block w-full h-full' },
  templateUrl: './aged-debt-bucket-chart.component.html',
})
export class AgedDebtBucketChartComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() data: AgedDebtBucketItem[] = [];
  @Input() selectedBucket: string | null = null;
  @Output() bucketSelect = new EventEmitter<AgedDebtBucketItem>();

  options: EChartsOption | null = null;

  @ViewChild('chartContainer', { static: true }) private chartContainer?: ElementRef<HTMLElement>;

  private hasBuiltOptions = false;
  private themeSub?: Subscription;
  private chartInstance?: { resize: () => void };
  private resizeObserver?: ResizeObserver;
  private readonly themeService = inject(ThemeService);

  ngOnInit(): void {
    if (!this.hasBuiltOptions) this.buildOptions();
    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildOptions());
  }

  ngOnChanges(): void {
    this.buildOptions();
  }

  ngAfterViewInit(): void {
    if (!this.chartContainer?.nativeElement) return;
    this.resizeObserver = new ResizeObserver(() => this.chartInstance?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  onChartInit(instance: { resize: () => void }): void {
    this.chartInstance = instance;
    this.chartInstance.resize();
  }

  onChartClick(event: { dataIndex?: number }): void {
    const index = event.dataIndex;
    if (index === undefined) return;

    const bucket = this.data[index];
    if (bucket) this.bucketSelect.emit(bucket);
  }

  private buildOptions(): void {
    this.hasBuiltOptions = true;
    const rows = this.data ?? [];
    if (rows.length === 0) {
      this.options = null;
      return;
    }

    const c = chartColors();
    const bucketColor = (bucket: string): string => {
      const normalized = bucket.toLowerCase().replace(/\s+/g, '').replace(/[\u2013\u2014]/g, '-');
      if (/^0(?:-|to)30(?:days?)?$/.test(normalized)) return '#12B76A';
      if (/^31(?:-|to)60(?:days?)?$/.test(normalized)) return '#F59E0B';
      if (/^61(?:-|to)90(?:days?)?$/.test(normalized)) return '#F97316';
      if (/^(?:90\+|90plus|over90|>90)(?:days?)?$/.test(normalized)) return '#F04438';
      return c.accent;
    };
    const maximumAmount = Math.max(...rows.map(row => Number(row.amount) || 0), 1);
    const formatCurrency = (value: number) => new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(value);

    this.options = {
      grid: { left: 12, right: 24, top: 8, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: c.chartSurface,
        borderColor: c.cardBorder,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: { color: c.textPrimary, fontSize: 12 },
        extraCssText: 'box-shadow: 0 12px 28px rgba(0,0,0,0.16); border-radius: 8px;',
        valueFormatter: value => formatCurrency(Number(value)),
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: maximumAmount * 1.22,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: c.textSecondary,
          fontSize: 11,
          formatter: (value: number) => {
            if (value >= 1000000) return `${(value / 1000000).toFixed(1)}m`;
            if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
            return `${value}`;
          },
        },
        splitLine: { lineStyle: { color: c.cardBorder, type: 'dashed', opacity: 0.75 } },
      },
      yAxis: {
        type: 'category',
        data: rows.map(row => row.bucket),
        inverse: true,
        axisLine: { lineStyle: { color: c.cardBorder } },
        axisTick: { show: false },
        axisLabel: { color: c.textSecondary, fontSize: 12, fontWeight: 700 },
      },
      series: [
        {
          name: 'Bucket click area',
          type: 'bar',
          data: rows.map(() => maximumAmount),
          // Percentage of the category band, so the hit area tracks the bar as
          // the chart grows taller instead of staying a fixed 36px.
          barWidth: '86%',
          barMaxWidth: 64,
          cursor: 'pointer',
          tooltip: { show: false },
          itemStyle: { color: 'rgba(0, 0, 0, 0.001)' },
          emphasis: { disabled: true },
          z: 1,
        },
        {
          name: 'Outstanding',
          type: 'bar',
          data: rows.map(row => ({
            value: row.amount,
            itemStyle: {
              color: bucketColor(row.bucket),
              opacity: this.selectedBucket === null || this.selectedBucket === row.bucket ? 1 : 0.35,
              borderColor: this.selectedBucket === row.bucket ? c.textPrimary : 'transparent',
              borderWidth: this.selectedBucket === row.bucket ? 2 : 0,
            },
            label: { position: 'right', color: c.textPrimary },
          })),
          barGap: '-100%',
          // Was a fixed 22px, which left four thin bars stranded in a tall card
          // on large screens. Scaling with the band keeps the chart looking
          // deliberate at every height; the cap stops it going slab-like.
          barWidth: '52%',
          barMaxWidth: 40,
          cursor: 'pointer',
          label: {
            show: true,
            position: 'right',
            color: c.textPrimary,
            fontSize: 11,
            fontWeight: 800,
            formatter: (params: any) => formatCurrency(Number(params.value ?? 0)),
          },
          z: 2,
        },
      ],
    };
  }
}
